import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MfaChallenge from '../components/MfaChallenge';
import { Lock, User, Eye, EyeOff, LogIn, Clock } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import ExtendedSessionDisclaimerModal from '../components/ExtendedSessionDisclaimerModal';
import { useBranding } from '../contexts/BrandingContext';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaPending, setMfaPending] = useState(null);

  // Check for OAuth MFA pending redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mfa') === 'pending') {
      fetch('/api/auth/mfa-pending', { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          if (data.pendingUserId) setMfaPending(data.pendingUserId);
        })
        .catch(() => {});
    }
  }, []);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthStatus, setOauthStatus] = useState({ google: false, microsoft: false });
  const [oauthError, setOauthError] = useState('');
  
  // Extended session state
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);

  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthStore();
  const { branding, refreshBranding } = useBranding();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }

    const params = new URLSearchParams(window.location.search);
    const urlError = params.get('error');
    if (urlError === 'oauth_failed') setOauthError('OAuth login failed. Please try again.');
    else if (urlError === 'not_registered') setOauthError('Account not found. Please register first.');
    else if (urlError === 'already_registered') setOauthError('Account already exists. Please login.');
    else if (urlError === 'no_email') setOauthError('Could not get email from OAuth provider.');

    fetch('/api/oauth/status')
      .then(res => res.json())
      .then(data => setOauthStatus(data))
      .catch(() => {});
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // If "keep me logged in" is checked, show disclaimer first
    if (keepLoggedIn && !pendingLogin) {
      setShowDisclaimerModal(true);
      return;
    }

    await performLogin();
  };

  const performLogin = async (disclaimerAcceptedAt = null) => {
    setLoading(true);
    setPendingLogin(false);

    const result = await login(username, password, {
      extendSession: keepLoggedIn && disclaimerAcceptedAt !== null,
      disclaimerAcceptedAt
    });

    if (result.mfaRequired) {
      setMfaPending(result.pendingUserId);
      setLoading(false);
      return;
    }
    if (result.success) {
      // Pull org-specific branding now that we know the user's organizationId
      try { await refreshBranding(useAuthStore.getState().user?.organizationId); } catch {}
      if (result.mustChangePassword) {
        navigate('/profile?forceChangePassword=true');
      } else {
        navigate('/');
      }
    } else {
      setError(result.error || 'Invalid credentials');
    }

    setLoading(false);
  };

  const handleDisclaimerAccept = (acceptedAt) => {
    setShowDisclaimerModal(false);
    setPendingLogin(true);
    performLogin(acceptedAt);
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimerModal(false);
    setKeepLoggedIn(false);
  };

  const googleLoginUrl = '/api/auth/google?action=login';
  const microsoftLoginUrl = '/api/auth/microsoft?action=login';

  return (
    <div
      className="login-page"
      style={branding.brandLoginBg ? { background: branding.brandLoginBg } : undefined}
    >
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo-wrapper">
            {branding.brandLogoUrl ? (
              <img src={branding.brandLogoUrl} alt={`${branding.brandName} Logo`} className="login-logo-img" />
            ) : (
              <>
                <img src="/serverliccm-logo-dark.png" alt={`${branding.brandName} Logo`} className="login-logo-img logo-light-theme" />
                <img src="/serverliccm-logo.png" alt={`${branding.brandName} Logo`} className="login-logo-img logo-dark-theme" />
              </>
            )}
          </div>

          <p>Security Dashboard</p>
        </div>

        {mfaPending ? (
        <MfaChallenge
          pendingUserId={mfaPending}
          onSuccess={(data) => {
            setMfaPending(null);
            // Set auth state and navigate
            const { login: authLogin, ...rest } = useAuthStore.getState();
            useAuthStore.setState({ user: data.user, isAuthenticated: true, mustChangePassword: data.mustChangePassword || false });
            if (data.mustChangePassword) {
              navigate("/profile?forceChangePassword=true");
            } else {
              navigate("/");
            }
          }}
          onBack={() => setMfaPending(null)}
        />
      ) : (
      <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              <User size={16} />
              Username
            </label>
            <input
              type="text"
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <Lock size={16} />
              Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Keep me logged in checkbox */}
          <label 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: keepLoggedIn ? 'var(--accent-alpha-08)' : 'var(--bg-surface)',
              borderRadius: '8px',
              border: `0.5px solid ${keepLoggedIn ? 'var(--accent)' : 'var(--border)'}`,
              transition: 'all 0.2s'
            }}
          >
            <input
              type="checkbox"
              checked={keepLoggedIn}
              onChange={(e) => setKeepLoggedIn(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                accentColor: 'var(--accent)',
                cursor: 'pointer'
              }}
            />
            <Clock size={16} color={keepLoggedIn ? '#10b981' : '#888'} />
            <span style={{ 
              color: keepLoggedIn ? '#10b981' : '#aaa', 
              fontSize: '14px',
              userSelect: 'none'
            }}>
              Keep me logged in for 30 days
            </span>
          </label>

          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={loading}
          >
            {loading ? 'Loading...' : <><LogIn size={18} /> Sign In</>}
          </button>
        </form>
      )}

        {oauthError && (
          <div className="login-error">
            {oauthError}
          </div>
        )}

        {(oauthStatus.google || oauthStatus.microsoft) && (
          <div className="oauth-section">
            <div className="oauth-label">— or continue with —</div>
            <div className="oauth-buttons">
              {oauthStatus.google && (
                <a href={googleLoginUrl} className="oauth-btn">
                  <img src="https://www.google.com/favicon.ico" alt="Google" />
                  Google
                </a>
              )}
              {oauthStatus.microsoft && (
                <a href={microsoftLoginUrl} className="oauth-btn">
                  <img src="https://www.microsoft.com/favicon.ico" alt="Microsoft" />
                  Microsoft
                </a>
              )}
            </div>
          </div>
        )}

        <div className="login-links">
          <Link to="/forgot-password" className="login-link">
            Forgot password?
          </Link>
          <Link to="/register" className="login-link primary">
            Don't have an account? Register
          </Link>
        </div>

        {branding.isWhiteLabeled && branding.poweredByVisible && (
          <div className="powered-by-footer">
            Powered by Serverli
          </div>
        )}
        {!branding.isWhiteLabeled && (
          <div className="login-footer">
            <p>Powered by Serverli</p>
          </div>
        )}
      </div>

      {/* Extended Session Disclaimer Modal */}
      <ExtendedSessionDisclaimerModal
        isOpen={showDisclaimerModal}
        onAccept={handleDisclaimerAccept}
        onCancel={handleDisclaimerCancel}
      />
    </div>
  );
};

export default Login;
